/**
 * Form Validation Utilities
 * 
 * Provides validation functions and error messages
 */

export interface ValidationRule {
  required?: boolean;
  minLength?: number;
  maxLength?: number;
  pattern?: RegExp;
  custom?: (value: any) => boolean | string;
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

export function validateField(
  value: any,
  rules: ValidationRule,
  fieldName: string
): ValidationResult {
  const errors: string[] = [];

  if (rules.required && (!value || (typeof value === 'string' && value.trim() === ''))) {
    errors.push(`${fieldName} is verplicht`);
  }

  if (value && typeof value === 'string') {
    if (rules.minLength && value.length < rules.minLength) {
      errors.push(`${fieldName} moet minimaal ${rules.minLength} tekens bevatten`);
    }

    if (rules.maxLength && value.length > rules.maxLength) {
      errors.push(`${fieldName} mag maximaal ${rules.maxLength} tekens bevatten`);
    }

    if (rules.pattern && !rules.pattern.test(value)) {
      errors.push(`${fieldName} heeft een ongeldig formaat`);
    }
  }

  if (rules.custom) {
    const customResult = rules.custom(value);
    if (customResult !== true) {
      errors.push(typeof customResult === 'string' ? customResult : `${fieldName} is ongeldig`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
}

export function validateForm<T extends Record<string, any>>(
  values: T,
  rules: Record<keyof T, ValidationRule>
): ValidationResult {
  const errors: string[] = [];

  Object.keys(rules).forEach((key) => {
    const fieldErrors = validateField(values[key], rules[key], key);
    errors.push(...fieldErrors.errors);
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
}
